import React, { useState } from 'react';
import { Modal, Button } from '../shared/Modal';
import { Field, TextInput, Select, CheckboxGroup, ToggleSwitch, Divider, SectionTitle } from '../shared/FormFields';
import { Monogram } from './teamUi';

// Invite a new member or change an existing one's role and access — one body,
// two modes, because the controls are identical and only the identity row
// differs.
//
// Role lives here rather than as an icon in the table row: promoting someone is
// a decision about that person, made in the same place as everything else about
// them, and it reads far better next to the access they will have.
//
// Ownership moves through this dialog too, from the row of the person who is
// GAINING it — choose "Owner" and the primary button hands off to the transfer
// confirmation instead of saving. The current owner's own row has no dialog:
// there is nothing about them to edit, and "transfer" asked from their row would
// need a second person-picker to mean anything.
//
// Account level is a toggle rather than another checkbox: it is a mode (every
// workspace, including ones created later), not one of N items. It doesn't
// disable the per-workspace boxes — the server accepts both grants together, so
// turning it off leaves the explicit picks intact.
//
// Mount with a key (member email or 'invite') so the form state resets per target.

const note = { margin: '6px 0 0', fontSize: '12px', lineHeight: 1.6, color: 'var(--text-muted, #94a3b8)' };

const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', member: 'Member' };

const ROLE_HINTS = {
  member: "Uses the app in the workspaces granted below. Can't manage the team, workspaces or billing.",
  admin: 'Invites and removes people, creates and manages every workspace, and uses every feature. No billing, and cannot transfer ownership.',
  owner: 'Makes this person the account owner. You become an Admin. You will be asked to confirm.',
};

export default function MemberAccessModal({
  mode = 'invite',
  email: initialEmail = '',
  accountName,
  workspaces = [],
  initialWorkspaceIds = [],
  initialAccountLevel = false,
  initialRole = 'member',
  canAssignRole = false,
  canAssignOwner = false,
  busy = false,
  onSubmit,
  onClose,
}) {
  const inviting = mode === 'invite';
  const [email, setEmail] = useState(initialEmail);
  const [workspaceIds, setWorkspaceIds] = useState(initialWorkspaceIds);
  const [accountLevel, setAccountLevel] = useState(initialAccountLevel);
  const [accountRole, setAccountRole] = useState(initialRole);

  // Picking "Owner" is not a save — it hands off to the type-to-confirm dialog.
  const transferring = accountRole === 'owner';
  const roleOptions = [
    { value: 'member', label: 'Member' },
    { value: 'admin', label: 'Admin' },
    // Ownership can only be moved, never granted alongside something else, so
    // this appears only for someone allowed to move it — and an invite can't
    // carry it at all, since the person doesn't exist on the account yet.
    ...(canAssignOwner && !inviting ? [{ value: 'owner', label: 'Owner — transfers ownership' }] : []),
  ];

  // An Admin needs no explicit workspace grants — they have the whole account —
  // so the "pick something" rule only applies to Members.
  const canSave = !busy
    && (!inviting || email.trim().length > 0)
    && (accountRole !== 'member' || accountLevel || workspaceIds.length > 0);

  const submit = () => {
    if (transferring) {
      onSubmit({ intent: 'transfer', email: (inviting ? email : initialEmail).trim() });
      return;
    }
    if (!canSave) return;
    // Omitted entirely when this caller can't set roles — a workspace owner may
    // edit access inside their workspace, and the server rejects the whole
    // request if it carries a role they aren't allowed to change.
    onSubmit({ email: email.trim(), workspaceIds, accountLevel, ...(canAssignRole ? { accountRole } : {}) });
  };

  const primaryLabel = busy
    ? 'Saving…'
    : transferring
      ? 'Transfer ownership…'
      : inviting ? 'Send invite' : 'Save access';

  return (
    <Modal
      title={inviting ? 'Invite a team member' : 'Edit member'}
      width={520}
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={transferring ? busy : !canSave}>
            {primaryLabel}
          </Button>
        </>
      )}
    >
      {inviting ? (
        <Field label="Email address">
          <TextInput type="email" value={email} onChange={setEmail} placeholder="teammate@company.com" />
        </Field>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <Monogram label={initialEmail} size={32} />
          <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary, #0f172a)', wordBreak: 'break-all' }}>
            {initialEmail}
          </span>
        </div>
      )}

      {/* A workspace owner sees the role but can't change it — they manage
          access to their workspace, not the person's standing in the account. */}
      <Field label="Role" hint={ROLE_HINTS[accountRole]}>
        <Select
          value={accountRole}
          onChange={setAccountRole}
          disabled={!canAssignRole}
          options={canAssignRole ? roleOptions : [{ value: accountRole, label: ROLE_LABELS[accountRole] }]}
        />
      </Field>

      <Divider />
      <SectionTitle>Access</SectionTitle>

      {accountRole === 'admin' ? (
        <p style={{ ...note, marginTop: 0 }}>
          Admins can open every workspace in this account, including ones created later.
        </p>
      ) : transferring ? (
        <p style={{ ...note, marginTop: 0 }}>
          The account owner always has full access, so there is nothing to choose here.
        </p>
      ) : (
        <>
          <div style={{ marginBottom: '18px' }}>
            <ToggleSwitch
              checked={accountLevel}
              onChange={() => setAccountLevel((v) => !v)}
              label={`${accountName || 'Account'} · account level`}
            />
            <p style={note}>Can open every workspace in this account, including ones you create later.</p>
          </div>

          <Field label="Workspaces">
            {workspaces.length === 0 ? (
              <p style={note}>No workspaces yet — create one first, or grant account-level access.</p>
            ) : (
              <>
                <CheckboxGroup
                  value={workspaceIds}
                  onChange={setWorkspaceIds}
                  options={workspaces.map((w) => ({ value: w.id, label: w.name || w.id }))}
                />
                {accountLevel && <p style={note}>Account level already covers every workspace.</p>}
              </>
            )}
          </Field>
        </>
      )}

      {inviting && (
        <p style={{ margin: '4px 0 0', fontSize: '12px', lineHeight: 1.6, color: 'var(--text-secondary, #64748b)' }}>
          People who already have an account get access straight away. Everyone else receives an email, and
          the access applies when they sign up with this address.
        </p>
      )}
    </Modal>
  );
}
